import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { AxiosError } from "axios";
import CustomTextField from "./CustomTextField";
import useCompanyLookup from "../hooks/useCompanyCondition";
import type {
  CompanyConditionListResponse,
  CompanyInfo,
  CompanyListInfo,
  CompanyStoreCamelInfo,
  CompanyStoreInfo,
  RegisterCompanyArgs,
  StoreInfo,
} from "../types";
import { checkHalfKanaOnly, removeEmptyValues } from "../utils/common";

const commonTableCellStyles = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 500,
  fontSize: 11,
  letterSpacing: "0.01071em",
  display: "table-cell",
  verticalAlign: "inherit",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  textAlign: "left",
  padding: "16px",
  paddingTop: "20px",
  paddingBottom: "20px",
  color: "rgba(0, 0, 0, 0.87)",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  maxWidth: "110px",
  borderLeft: "1px solid rgb(224, 224, 224)",
  borderTop: "1px solid rgb(224, 224, 224)",
};

type CompanyRegisterProps = {
  open: boolean;
  isSearching: boolean;
  isRegistering: boolean;
  bgColor: string;
  onClose: () => void;
  onRegister: (params: RegisterCompanyArgs) => Promise<void>;
};

const headerStyles = {
  ...commonTableCellStyles,
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#fbefb4",
};

const textStyle = {
  textAlign: "right",
  marginRight: "16px",
};

/**
 * 企業登録モーダル
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
function CompanyRegisterModal({
  open,
  isSearching,
  isRegistering,
  bgColor,
  onClose,
  onRegister,
}: CompanyRegisterProps) {
  const [centerCd, setCenterCd] = useState<number | undefined>(undefined);
  const [storeCds, setStoreCds] = useState(Array(2).fill(""));
  const [storeNames, setStoreNames] = useState(Array(2).fill(""));
  const [showSeachTable, setShowSeachTable] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);


  const [selectedCompany, setSelectedCompany] = useState<CompanyInfo>();
  const [companyList, setCompanyList] = useState<CompanyConditionListResponse[]>([]);
  // const [selectedStores, setSelectedStores] = useState<StoreInfo[]>([]);
  const { params, setParams, list, setList, isLoading, companyLookup } = useCompanyLookup();
  const { showAlert } = useContext(SuccessAlertContext);

  const handleCompanyRegister = async () => {
    if (!selectedCompany) {
      showAlert("企業を選択してください。", "error");
      return;
    }
    if (!storeNames.every(sName => checkHalfKanaOnly(sName))) {
      showAlert("店舗名は全て半角ｶﾅで入力してください。", "error");
      return;
    }
    try {
      await onRegister(removeEmptyValues({
        companyCd: selectedCompany.companyCd,
        centerCd: centerCd ? String(centerCd) : undefined,
        stores: selectedCompany.stores.map((s, i) => ({
          client_cd: s.clientCd,
          client_store_cd: storeCds[i],
          client_store_name: storeNames[i],
          client_store_temp: "", // 使用しないので削除可能
        })),
      }));
      onClose();
    } catch (err) {
      console.error(err);
      const error2 = err as AxiosError;
      // レスポンス自体がなくエラーになった時
      if (!error2.response) {
        Notify.setNotification({
          show: true,
          title: "APIに接続できませんでした。",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
        return;
      }
      const { status } = error2.response;
      const data = error2.response.data as {code: string, message: string};
      switch (status) {
        // case 400:
        //   Notify.setNotification({
        //     show: true,
        //     title: "パラメータエラーです。",
        //     confirmLabel: "OK",
        //     actionsPosition: "end",
        //   });
        //   break;
        case 422:
          Notify.setNotification({
            // show: true,
            // title: data.messages.join('\n'),
            // confirmLabel: "OK",
            // actionsPosition: "end",
            show: true,
            title: "入力エラーがあります。",
            message: data.message,
            titleColor: "var(--error)",
          });
          break;
        default:
          break;
      }
    }
  };

  // 検索フィルター
  const handleLookupClick = () => {
    if (params.company_cd.trim() === "" && params.company_name.trim() === "") {
      // setParams((p) => ({ ...p, company_name: "" }));
      showAlert('企業コードまたは企業名は入力必須です。', 'error');
      setError(true);
      return;
    }
    setError(false);
    setShowSeachTable(true);
    companyLookup();
  };

  /* const handleSearchClick = async () => {
    setShowSeachTable(false);
    setCompanyList([]);
    setLoading(true);
    setError(null);

    try {
      const { list } = await companySelectApi({
        limit: 20,
        offset: 0,
        company_name: companyName.trim() || undefined,
        company_cd: companyCd != null ? String(companyCd) : undefined,
        center_cd: centerCd != null ? String(centerCd) : undefined,
      });
      setCompanyList(list);
      setShowSeachTable(true);
    } catch (err) {
      console.error(err);
      return;
    } finally {
      setLoading(false);
    }
  }; */

  // サイドリスト選択処理
  const handleRowClick = (company: CompanyInfo) => {
    const stores: CompanyStoreCamelInfo[] = company.stores || [];

    setSelectedCompany(company);
    // const newCds = stores.map((s) => s.client_store_cd || "");
    // const newNames = stores.map((s) => s.client_store_name || "");
    setStoreCds(stores.map((s) => s.clientCd));
    setStoreNames(stores.map((s) => checkHalfKanaOnly(s.storeName) ? s.storeName : ""));
  };

  // const hasResult = !isSearching && list.length > 0;

  const tableHeadElement = () => (
    <TableRow>
      <TableCell sx={headerStyles}>店舗名</TableCell>
      <TableCell sx={headerStyles}>店舗コード（先方）</TableCell>
      <TableCell sx={headerStyles}>店舗名（先方）</TableCell>
    </TableRow>
  );

  const tableBodyElement = () => {
    if (!selectedCompany || !selectedCompany.stores || selectedCompany.stores.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} align="center">
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              会社に関する情報はありません
            </Typography>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <TableBody>
        {selectedCompany.stores.map((store, index) => (
          <TableRow key={store.clientCd}>
            <TableCell>{store.storeName}</TableCell>
            <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
              <CustomTextField
                id="storeCds"
                type="text"
                label="例）0001"
                text={storeCds[index] || ""}
                setText={(newValue) => {
                  const updatedValues = [...storeCds];
                  updatedValues[index] = newValue;
                  setStoreCds(updatedValues);
                }}
                maxLength={50}
                size="small"
                isNumericText
              />
            </TableCell>
            <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
              <CustomTextField
                id="storeNames"
                type="text"
                label="例）ｺﾊﾞｯｸ（半角ｶﾅ）"
                text={storeNames[index] || ""}
                setText={(newValue) => {
                  const updatedValues = [...storeNames];
                  updatedValues[index] = newValue;
                  setStoreNames(updatedValues);
                }}
                maxLength={100}
                size="small"
                allowHalfKanaOnly
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const searchTableHeadElement = () => (
    <TableRow>
      <TableCell sx={headerStyles}>企業コード</TableCell>
      <TableCell sx={headerStyles}>企業名</TableCell>
    </TableRow>
  );

  const searchTableBodyElement = () => {
    if (list.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} align="center">
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              検索企業はまだありません
            </Typography>
          </TableCell>
        </TableRow>
      );
    }
    return list.map((c) => (
      <TableBody>
        <TableRow
          key={c.companyCd}
          hover
          sx={{ cursor: "pointer" }}
          onClick={() => handleRowClick(c)}
        >
          <TableCell sx={{ backgroundColor: bgColor }}>{c.companyCd}</TableCell>
          <TableCell sx={{ backgroundColor: bgColor }}>{c.companyName}</TableCell>
        </TableRow>
      </TableBody>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="lg"
      PaperProps={{
        sx: showSeachTable
          ? {
              height: "calc(100vh-64px)",
              maxHeight: "calc(100vh-64px)",
            }
          : {
              maxHeight: "90vh",
            },
      }}
    >
      <Box p={2}>
        <DialogTitle>企業登録</DialogTitle>
        <Box display="flex">
          <DialogContent sx={{ overflow: "visible" }}>
            {(isLoading || isRegistering) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <CircularProgress size={100} />
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                margin={2}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "190px 230px 1fr",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Typography>企業名：</Typography>
                <Box sx={{ gridColumn: "2/span 2" }}>
                  <CustomTextField
                    id="companyName"
                    label="例）コバック"
                    type="text"
                    text={params.company_name}
                    setText={(value) =>
                      setParams((p) => ({
                        ...p,
                        company_name: value,
                      }))
                    }
                    maxLength={100}
                    size="small"
                    fullWidth
                    error={error}
                    // isHalfWidthKatakanaText
                  />
                </Box>
                <Typography>企業コード：</Typography>
                <CustomTextField
                  id="companyCd"
                  label="例）000001"
                  type="text"
                  text={params.company_cd}
                  setText={(value) =>
                    setParams((p) => ({
                      ...p,
                      company_cd: value,
                    }))
                  }
                  maxLength={12}
                  size="small"
                  fullWidth
                  isNumericText
                  error={error}
                />
                <Button variant="contained" onClick={handleLookupClick}>
                  {isLoading ? <CircularProgress size={10} /> : "検索"}
                </Button>
              </Box>
            </Box>
            {/* {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )} */}

            {/* センターコード */}
            <Box
              mt={3}
              display="flex"
              flexDirection="row"
              borderBottom={2}
              borderColor="primary.main"
              justifyContent="flex-start"
            >
              <Typography>センターコード</Typography>
            </Box>
            <Box
              margin={2}
              sx={{
                display: "grid",
                gridTemplateColumns: "190px 230px 1fr",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Typography>センターコード：</Typography>
              <Box sx={{ gridColumn: "2/span 2" }}>
                <CustomTextField
                  id="centerCd"
                  label="例）0001"
                  type="text"
                  text={centerCd != null ? String(centerCd) : ""}
                  setText={setCenterCd}
                  maxLength={50}
                  size="small"
                  fullWidth
                  isNumericText
                />
              </Box>
            </Box>

            {/* 先方の店舗情報 */}
            <Box
              mt={3}
              display="flex"
              flexDirection="row"
              borderBottom={2}
              borderColor="primary.main"
              justifyContent="flex-start"
            >
              <Typography>先方の店舗情報</Typography>
            </Box>
            <Box mt={2}>
              <TableContainer component={Paper} sx={{ overflow: "visible", width: "100%" }}>
                <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="mock_table">
                  <TableHead>{tableHeadElement()}</TableHead>
                  {tableBodyElement()}
                </Table>
              </TableContainer>
            </Box>

            <DialogActions
              sx={{
                mt: 2,
              }}
            >
              <Button variant="custom-outlined" onClick={() => onClose()}>
                キャンセル
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  await handleCompanyRegister();
                }}
              >
                登録
              </Button>
            </DialogActions>
          </DialogContent>

          {/* 右側企業テーブル */}
          {showSeachTable && (
            <DialogContent sx={{ overflow: "visible" }}>
              <Box
                border="solid 2px"
                sx={{ width: "300px", height: "90%", padding: 2, mt: 2, overflow: "auto" }}
              >
                <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
                  <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="mock_table">
                    <TableHead>{searchTableHeadElement()}</TableHead>
                    {searchTableBodyElement()}
                  </Table>
                </TableContainer>
              </Box>
            </DialogContent>
          )}
        </Box>

        {/* <DialogActions>
          <Button variant="custom-outlined" onClick={() => handleClose()}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleCompanyRegister();
              handleClose();
            }}
          >
            登録
          </Button>
        </DialogActions> */}
      </Box>
    </Dialog>
  );
}

export default CompanyRegisterModal;
